import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  Folder, FolderOpen, HardDrive, Plus, Trash2, Music, 
  Upload, Search, Filter, ChevronRight, Disc3
} from 'lucide-react';

export default function LibraryManager() {
  const [crates, setCrates] = useState([]);
  const [selectedCrate, setSelectedCrate] = useState(null);
  const [crateTracks, setCrateTracks] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [showNewCrateDialog, setShowNewCrateDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [newCrateName, setNewCrateName] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [recursive, setRecursive] = useState(true);
  const [createCrate, setCreateCrate] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCrates();
    loadAllTracks();
  }, []);

  const loadCrates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrates(response.data);
    } catch (error) {
      console.error('Failed to load crates:', error);
    }
  };

  const loadAllTracks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTracks(response.data);
    } catch (error) {
      console.error('Failed to load tracks:', error);
    }
  };

  const loadCrateTracks = async (crateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crates/${crateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrateTracks(response.data.tracks || []);
      setSelectedCrate(response.data);
    } catch (error) {
      console.error('Failed to load crate tracks:', error);
    }
  };

  const createNewCrate = async () => {
    if (!newCrateName.trim()) {
      toast.error('Please enter a crate name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crates`, {
        name: newCrateName,
        color: '#3b82f6',
        icon: 'folder'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Crate "${newCrateName}" created`);
      setNewCrateName('');
      setShowNewCrateDialog(false);
      loadCrates();
    } catch (error) {
      toast.error('Failed to create crate');
      console.error(error);
    }
  };

  const deleteCrate = async (crateId) => {
    if (!confirm('Delete this crate? (Tracks will remain in library)')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crates/${crateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Crate deleted');
      loadCrates();
      if (selectedCrate?.id === crateId) {
        setSelectedCrate(null);
        setCrateTracks([]);
      }
    } catch (error) {
      toast.error('Failed to delete crate');
      console.error(error);
    }
  };

  const scanExternalFolder = async () => {
    if (!folderPath.trim()) {
      toast.error('Please enter a folder path');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/library/scan-folder`, {
        folder_path: folderPath,
        create_crate: createCrate,
        recursive: recursive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(
        `✓ Scanned ${response.data.total_files} files\n` +
        `Added: ${response.data.added}\n` +
        `Skipped: ${response.data.skipped}`,
        { duration: 5000 }
      );

      setFolderPath('');
      setShowFolderDialog(false);
      loadCrates();
      loadAllTracks();

      // Auto-select the new crate
      if (response.data.crate_id) {
        loadCrateTracks(response.data.crate_id);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to scan folder';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Browse and upload files directly (NO PATHS NEEDED!)
  const browseAndUploadFolder = async () => {
    // Check if running in Electron (has native folder picker with real paths!)
    if (window.electronAPI && window.electronAPI.selectFolder) {
      try {
        toast.info('Opening native folder picker...');
        
        // Use Electron's native folder picker - GETS REAL PATH!
        const folderPath = await window.electronAPI.selectFolder();
        
        if (!folderPath) {
          // User cancelled
          return;
        }
        
        toast.success(`Selected: ${folderPath}`);
        console.log('Electron folder path:', folderPath);
        
        // Now we have the REAL PATH! Send to backend
        setLoading(true);
        setShowFolderDialog(false);
        
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/library/scan-folder`, {
            folder_path: folderPath,
            create_crate: createCrate,
            recursive: true
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          toast.success(
            `✓ Scanned ${response.data.total_files} files\n` +
            `Added: ${response.data.added}`,
            { duration: 5000 }
          );

          loadCrates();
          loadAllTracks();

          if (response.data.crate_id) {
            loadCrateTracks(response.data.crate_id);
          }
        } catch (error) {
          console.error('Scan error:', error);
          toast.error(`Failed to scan folder: ${error.response?.data?.detail || error.message}`);
        } finally {
          setLoading(false);
        }
        
        return;
      } catch (error) {
        console.error('Electron folder picker error:', error);
        toast.error('Failed to open folder picker');
      }
    }
    
    // Fallback: Browser upload method
    // Create a new file input for folder selection
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.accept = '.mp3,.wav,.flac,.m4a,.aac,.ogg,.wma,audio/*';
    
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      const audioFiles = files.filter(f => {
        const ext = f.name.toLowerCase().split('.').pop();
        return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
      });
      
      if (audioFiles.length === 0) {
        toast.error('No audio files found in selected folder');
        return;
      }

      // Get folder name from first file
      const folderName = audioFiles[0].webkitRelativePath.split('/')[0] || 'My Music';
      
      toast.info(`Found ${audioFiles.length} audio files. Uploading...`, { duration: 3000 });
      setLoading(true);
      setShowFolderDialog(false);

      try {
        // Upload in chunks of 20 files
        const chunkSize = 20;
        let uploaded = 0;
        
        for (let i = 0; i < audioFiles.length; i += chunkSize) {
          const chunk = audioFiles.slice(i, i + chunkSize);
          const formData = new FormData();
          
          chunk.forEach(file => {
            formData.append('files', file);
          });

          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/library/upload/multiple?scan=true`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            },
            timeout: 300000 // 5 minutes
          });

          uploaded += response.data.total_uploaded;
          toast.info(`Uploaded ${uploaded}/${audioFiles.length} files...`, { duration: 2000 });
        }

        // Create crate with uploaded tracks
        if (createCrate) {
          const token = localStorage.getItem('token');
          const crateName = folderName;
          const crateResponse = await axios.post(`${API}/crates`, {
            name: crateName,
            description: `Imported from folder: ${folderName}`,
            color: '#3b82f6',
            icon: 'folder'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Get all tracks and add to crate
          const tracksResponse = await axios.get(`${API}/tracks`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const recentTracks = tracksResponse.data.slice(-uploaded); // Last N uploaded tracks
          
          for (const track of recentTracks) {
            await axios.post(`${API}/crates/${crateResponse.data.id}/tracks/${track.id}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }

          toast.success(`✓ Created crate "${crateName}" with ${uploaded} tracks!`, { duration: 5000 });
          
          loadCrates();
          loadAllTracks();
          loadCrateTracks(crateResponse.data.id);
        } else {
          toast.success(`✓ Uploaded ${uploaded} tracks!`, { duration: 5000 });
          loadAllTracks();
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload files: ${error.response?.data?.detail || error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    input.click();
  };

  // NEW: Smart folder browser that tries to get path
  const browseFolder = async () => {
    try {
      // Check if File System Access API is supported (Chrome/Edge)
      if ('showDirectoryPicker' in window) {
        toast.info('Opening folder picker...');
        
        const dirHandle = await window.showDirectoryPicker({
          mode: 'read'
        });
        
        // Try to get path from first file
        let foundPath = null;
        for await (const entry of dirHandle.values()) {
          if (entry.kind === 'file') {
            const file = await entry.getFile();
            
            // Check if File has path property (Electron or some environments)
            if (file.path) {
              const fullPath = file.path;
              foundPath = fullPath.substring(0, fullPath.lastIndexOf('/'));
              break;
            }
          }
        }
        
        if (foundPath) {
          // Success! We got the full path
          setFolderPath(foundPath);
          toast.success(`✓ Path detected: ${foundPath}`, { duration: 4000 });
        } else {
          // Browser security prevents path access
          toast.warning(
            `Selected folder: "${dirHandle.name}"\n\n` +
            `⚠️ Browser security prevents automatic path detection.\n` +
            `Please copy the full path from File Explorer/Finder and paste below.`,
            { duration: 8000 }
          );
          
          // Show instructions
          toast.info(
            `📝 How to copy path:\n` +
            `Windows: Right-click folder → Properties → Location\n` +
            `Mac: Right-click → Get Info → Copy path`,
            { duration: 10000 }
          );
        }
      } else {
        // Fallback to input[type=file] method
        fileInputRef.current?.click();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled - ignore
        return;
      }
      console.error('Error browsing folder:', error);
      toast.error('Failed to browse. Please enter path manually.');
    }
  };

  // Handle folder selection via file input (for browser compatibility)
  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Get the common path from the first file
      const path = files[0].webkitRelativePath || files[0].path;
      if (path) {
        const folderName = path.split('/')[0];
        setFolderPath(folderName);
        toast.info(`Selected folder: ${folderName}\nReady to scan ${files.length} files`);
      }
    }
  };

  const filteredTracks = crateTracks.filter(track =>
    track.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    track.artist.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Library Manager</h1>
              <p className="text-gray-400">Organize your music with virtual crates</p>
            </div>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-gray-700 hover:bg-gray-600">
              ← Back to Dashboard
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => setShowFolderDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <HardDrive className="w-4 h-4 mr-2" />
              Load External Folder
            </Button>
            <Button onClick={() => setShowNewCrateDialog(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              New Crate
            </Button>
            <Badge className="bg-purple-600 text-white px-3 py-2">
              {allTracks.length} Total Tracks
            </Badge>
            <Badge className="bg-cyan-600 text-white px-3 py-2">
              {crates.length} Crates
            </Badge>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-4">
          {/* Crates Sidebar */}
          <div className="col-span-3">
            <Card className="bg-gray-900/80 border-cyan-500/30">
              <CardContent className="p-4">
                <h2 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                  <Folder className="w-5 h-5" />
                  Crates
                </h2>

                <ScrollArea className="h-[calc(100vh-300px)]">
                  {crates.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <Folder className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No crates yet</p>
                      <p className="text-xs mt-1">Create one or load a folder</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {crates.map((crate) => (
                        <div
                          key={crate.id}
                          className={`p-3 rounded-lg cursor-pointer transition-all group ${
                            selectedCrate?.id === crate.id
                              ? 'bg-cyan-600/30 border border-cyan-500'
                              : 'bg-gray-800/50 hover:bg-gray-800 border border-transparent'
                          }`}
                          onClick={() => loadCrateTracks(crate.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <FolderOpen className="w-4 h-4 text-cyan-400" />
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-semibold truncate">{crate.name}</p>
                                <p className="text-xs text-gray-400">{crate.track_ids?.length || 0} tracks</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCrate(crate.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 h-7 w-7 p-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Tracks View */}
          <div className="col-span-9">
            <Card className="bg-gray-900/80 border-purple-500/30">
              <CardContent className="p-4">
                {selectedCrate ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-2xl font-bold text-white">{selectedCrate.name}</h2>
                        <p className="text-gray-400 text-sm">{crateTracks.length} tracks</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            placeholder="Search tracks..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 bg-gray-800 border-gray-700 text-white w-64"
                          />
                        </div>
                      </div>
                    </div>

                    <ScrollArea className="h-[calc(100vh-300px)]">
                      {filteredTracks.length === 0 ? (
                        <div className="text-center text-gray-500 py-16">
                          <Music className="w-16 h-16 mx-auto mb-3 opacity-50" />
                          <p>No tracks in this crate</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredTracks.map((track) => (
                            <div
                              key={track.id}
                              className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-all border border-gray-700/50"
                            >
                              <div className="flex items-center gap-4">
                                <Disc3 className="w-10 h-10 text-purple-400" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white font-semibold truncate">{track.title}</p>
                                  <p className="text-gray-400 text-sm truncate">{track.artist}</p>
                                  <p className="text-xs text-gray-500 truncate mt-1">
                                    📁 {track.file_path}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  {track.bpm && (
                                    <Badge className="bg-cyan-900/30 text-cyan-400">
                                      {track.bpm} BPM
                                    </Badge>
                                  )}
                                  {track.key && (
                                    <Badge className="bg-pink-900/30 text-pink-400">
                                      {track.key}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </>
                ) : (
                  <div className="h-[calc(100vh-200px)] flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <FolderOpen className="w-24 h-24 mx-auto mb-4 opacity-30" />
                      <p className="text-lg">Select a crate to view tracks</p>
                      <p className="text-sm mt-2">Or load an external folder to get started</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* New Crate Dialog */}
      <Dialog open={showNewCrateDialog} onOpenChange={setShowNewCrateDialog}>
        <DialogContent className="bg-gray-900 border-cyan-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Create New Crate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Crate Name</Label>
              <Input
                value={newCrateName}
                onChange={(e) => setNewCrateName(e.target.value)}
                placeholder="e.g., House Music, Favorites, Party Mix"
                className="bg-gray-800 border-gray-700 text-white"
                onKeyPress={(e) => e.key === 'Enter' && createNewCrate()}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setShowNewCrateDialog(false)} className="bg-gray-700">
                Cancel
              </Button>
              <Button onClick={createNewCrate} className="bg-cyan-600 hover:bg-cyan-700">
                Create Crate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load Folder Dialog */}
      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent className="bg-gray-900 border-blue-500/30 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-blue-400 flex items-center gap-2 text-2xl">
              <Folder className="w-6 h-6" />
              Load Music Folder
            </DialogTitle>
            <p className="text-sm text-gray-400 mt-2">
              Select a folder and we&apos;ll import all your music automatically
            </p>
          </DialogHeader>
          <div className="space-y-4">
            {/* MAIN METHOD: Browse and Upload */}
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-2 border-green-500 rounded-lg p-6 text-center">
              <Folder className="w-16 h-16 mx-auto mb-3 text-green-400" />
              <h3 className="text-xl font-bold text-green-300 mb-2">
                Click Button Below to Select Your Music Folder
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Navigate to your music folder, select it, and we&apos;ll handle everything automatically!
              </p>
              
              <Button
                type="button"
                onClick={browseAndUploadFolder}
                disabled={loading}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold text-lg px-8 py-6 h-auto"
              >
                {loading ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 mr-3" />
                    Select Folder & Import Music
                  </>
                )}
              </Button>
              
              <div className="mt-4 text-xs text-gray-400 space-y-1">
                <p>✓ Files will be analyzed automatically (BPM, Key)</p>
                <p>✓ Crate will be created with folder name</p>
                <p>✓ No manual typing needed!</p>
              </div>
            </div>

            {/* Options */}
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <Label className="text-white text-sm font-semibold">Import Options:</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="createCrate2"
                  checked={createCrate}
                  onCheckedChange={setCreateCrate}
                />
                <label htmlFor="createCrate2" className="text-sm text-white cursor-pointer">
                  Create crate automatically with folder name
                </label>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-300">
                <strong>💡 What happens:</strong> Select your folder → Files upload → System analyzes BPM/Key → Crate created → Ready to DJ!
              </p>
            </div>

            {/* Options moved above */}

            <div className="flex justify-center">
              <Button 
                onClick={() => setShowFolderDialog(false)} 
                variant="outline"
                className="bg-gray-800 border-gray-600 hover:bg-gray-700"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
